import {
  Component,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  HostListener,
  Inject,
} from '@angular/core';
import { ActiveCall, Call, CallTimers, CallMuteStatus } from 'app/models/dto/call.model';
import { slideOutUp, slideInUp, fadeIn } from 'ng-animate';
import { trigger, transition, useAnimation } from '@angular/animations';
import { Queue } from 'app/models/queue/queue.model';
import { Alert } from 'app/models/dto/alert.model';
import { DOCUMENT } from '@angular/common';
import { TwilioDeviceService } from 'app/twilio-device/twilio-device.service';
import { Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { PhoneLocaleService } from '../../services/phone-locale.service';

@Component({
  selector: 'app-call-display',
  templateUrl: './call-display.component.html',
  styleUrls: ['./call-display.component.scss'],
  animations: [
    trigger('animateIn', [
      transition(':enter', useAnimation(slideInUp, {params: {timing: 0.3}}))
    ]),
    trigger('animateOut', [
      transition(':leave', useAnimation(slideOutUp, {params: {timing: 0.2}}))
    ]),
    trigger('btnAnimateIn', [
      transition(':enter', useAnimation(fadeIn, {params: {timing: 0.3}}))
    ]),
  ],
  standalone: false
})
export class CallDisplayComponent implements OnChanges, OnInit {

  @Output() callAlertDismissed = new EventEmitter();
  @Output() callInitiated = new EventEmitter<Call>();
  @Output() callCanceled = new EventEmitter();
  @Output() callMuted = new EventEmitter();
  @Output() toggleSilentMonitorMute = new EventEmitter();
  @Output() placeCallOnHold = new EventEmitter();
  @Output() startSecondCall = new EventEmitter();
  @Output() transferCall = new EventEmitter();
  @Output() swapCalls = new EventEmitter();
  @Output() mergeCalls = new EventEmitter();
  @Output() completeCallWrapUp = new EventEmitter();
  @Output() openWrapUp = new EventEmitter<boolean>();
  @Output() queueCall = new EventEmitter<Queue>();
  @Input() unendedCall: boolean;
  @Input() activeCall: ActiveCall;
  @Input() secondaryCall: ActiveCall;
  @Input() dispositionCall: ActiveCall;
  @Input() callStateIsProcessing: boolean;
  @Input() twilioDeviceIsLoading: boolean;
  @Input() silentMonitorInProgress: boolean;
  @Input() canUnmuteSilentMonitorCall: boolean;
  @Input() callTimers: CallTimers;
  @Input() callIsMuted: CallMuteStatus;
  @Input() queue: Queue[];
  @Input() alerts: Alert[];
  @Input() timerOpen: boolean;
  @Output() timerOpenChange = new EventEmitter<boolean>();
  softphoneConnected$: Observable<boolean>;
  dataSource: MatTableDataSource<any>;
  displayedColumns: string[] = ['name', 'muteIcon', 'phoneNumber'];

  callDisplayOpen = false;
  cannotAddACall = true;
  checkEndCall = false;
  isMuted = false;
  callIsOnHold = false;
  additionalParticipantsDisplay = false;

  participantsDisplay: string | null;
  displayBusinessName: string;
  displayPhoneNumber: string;
  displayEndButton: boolean;

  constructor(
    @Inject(DOCUMENT) public document,
    private twilioDevice: TwilioDeviceService,
    private phoneLocaleService: PhoneLocaleService,
  ) {
    this.softphoneConnected$ = this.twilioDevice.connected$;
  }

  ngOnInit(): void {
    if (this.unendedCall) {
      this.openWrapUp.emit(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.toggleCallBar(changes);
    this.wrapUpOnEndedCall(changes);
    this.toggleTimer(changes);
    this.toggleOnHoldCall(changes);
    this.twoCallsAlreadyHappening(changes);
    this.displayParticipantData(changes);
    this.showEndCallBtn();

    this.isMuted = this.activeCall && this.activeCall.callId ? this.callIsMuted[this.activeCall.callId] : false;
  }

  @HostListener('window:scroll', ['$event'])
    onWindowScroll() {
      if (window.pageYOffset > 68) {
        const callbar = document.getElementById('call-container');
        const padding = document.getElementById('sticky-nav-padding');
        if (callbar && padding) {
          callbar.classList.add('sticky');
          padding.classList.add('sticky-nav-padding');
        }
      } else {
        const callbar = document.getElementById('call-container');
        const padding = document.getElementById('sticky-nav-padding');
        if (callbar && padding) {
          callbar.classList.remove('sticky');
          padding.classList.remove('sticky-nav-padding');
        }
      }
    }

  wrapUpOnEndedCall(changes: SimpleChanges): void  {
    if (changes.activeCall) {
      const { currentValue, previousValue } = changes.activeCall;
      if (
        previousValue &&
        currentValue &&
        currentValue.callState === 'disconnected' &&
        previousValue.callState !== 'disconnected' &&
        !/^(conference|internal|add) call/i.exec(<string> currentValue.disposition) &&
        !this.silentMonitorInProgress
      ) {
        // Only provide wrap up dialog if the call has callLegs
        // e.g. - someone answers/handles the call

        if (currentValue.callLegs) {
          this.twilioDevice.destroy();
          this.openWrapUp.emit(false);
        }
      }
      if (
        currentValue &&
        currentValue.callState === 'disconnected' &&
        (/^(conference|internal|add) call/i.exec(<string> currentValue.disposition) ||
        this.silentMonitorInProgress)
        ) {
        this.completeCallWrapUp.emit();
      }
    }

    if (changes.dispositionCall) {
      const { currentValue, previousValue } = changes.dispositionCall;
      if (!previousValue && currentValue) {
        this.openWrapUp.emit(false);
      }
    }
  }

  toggleCallBar(changes: SimpleChanges): void  {
    if (changes.activeCall || changes.queue) {
      if (this.activeCall) {
        if (
          changes.queue
          && this.queue.length > 0
          && !this.activeCall.inProgress
        ) {
          this.queueCall.emit(this.queue[0]);
        }
        this.callDisplayOpen = true;
      } else if (!this.activeCall && this.queue.length > 0) {
        this.callDisplayOpen = true;
        this.queueCall.emit(this.queue[0]);
      } else {
        this.callDisplayOpen = false;
      }
    }
  }

  toggleOnHoldCall(changes: SimpleChanges): void {
    if (changes.activeCall) {
      const { currentValue, previousValue } = changes.activeCall;
      if (previousValue && currentValue && currentValue.callState === 'on hold') {
        this.callIsOnHold = true;
      } else {
        this.callIsOnHold = false;
      }
    }
  }

  getDisplayPhoneNumber(call: ActiveCall): string {
    return this.phoneLocaleService.getPhoneNumberFromActiveCall(call, false);
  }

  getDisplayBusinessName(call: ActiveCall): string {
    return call.businessName ? call.businessName : 'No Current Call';
  }

  displayParticipantData(changes: SimpleChanges): void {
    if (changes.activeCall) {
      const { currentValue } = changes.activeCall;
      if (currentValue) {
        this.dataSource = new MatTableDataSource(currentValue.callLegs);
        this.displayPhoneNumber = this.getDisplayPhoneNumber(currentValue);
        this.displayBusinessName = this.getDisplayBusinessName(currentValue);
      }
      const numberOfParticipants = currentValue && currentValue.callLegs ? currentValue.callLegs.length : 0;
      this.additionalParticipantsDisplay = numberOfParticipants ? numberOfParticipants > 2 : false;
      this.participantsDisplay = this.additionalParticipantsDisplay ? `+${numberOfParticipants - 2} others` : null;
    }
  }

  twoCallsAlreadyHappening(changes: SimpleChanges): void {
    if (changes.activeCall || changes.secondaryCall) {
      this.cannotAddACall = !!this.activeCall && !!this.secondaryCall ? true : false;
    }
  }

  handleHoldCall(holdStatus: boolean): void {
    this.placeCallOnHold.emit(holdStatus);
  }

  handleTransferCall(callInfo: object): void {
    this.transferCall.emit(callInfo);
  }

  handleSecondCall(callInfo: object): void {
    this.startSecondCall.emit(callInfo);
  }

  swapTheCalls(): void {
    this.swapCalls.emit();
  }

  mergeTheCalls(): void {
    this.mergeCalls.emit();
  }

  toggleTimer(changes: SimpleChanges): void {
    if (changes.activeCall) {
      const { currentValue } = changes.activeCall;
      const shouldStartNewTimer = currentValue &&
        currentValue.callState !== 'canceled' &&
        (currentValue.callState === 'connected' || currentValue.inProgress);
      if (shouldStartNewTimer) {
        setTimeout(() => this.timerOpenChange.emit(true), 0);
      }
    }
  }

  endCallConfirmation(): void {
    this.openWrapUp.emit(true);
  }

  showEndCallBtn() {
    if (this.activeCall) {
      if (this.activeCall.inProgress &&
          (
            this.activeCall.callState === 'connected' ||
            this.activeCall.callState === 'on hold' ||
            this.activeCall.callState === 'call joined'
          )
        ) {
          if (this.activeCall.type === 'incoming' && this.activeCall.incomingCallType && this.activeCall.incomingCallType === 'softphone') {
            return this.displayEndButton = true;
          }
        return this.displayEndButton = true;
      }
    }
    return this.displayEndButton = false;
  }

  handleCall(phone: number): void {
    this.callInitiated.emit({
      borrowerId: this.activeCall.borrowerId,
      phone: phone,
      borrower: this.activeCall.borrower
    });
  }

  handleMute(setting: boolean): void {
    if (this.silentMonitorInProgress && this.canUnmuteSilentMonitorCall) {
      this.toggleSilentMonitorMute.emit(setting);
    } else if (!this.silentMonitorInProgress) {
      this.callMuted.emit(setting);
    }
  }

  handleCancelCall(): void {
    this.callCanceled.emit(this.activeCall.callId);
    this.timerOpenChange.emit(false);
  }

  handleDismissCallAlert(alertIndex: number): void {
    this.callAlertDismissed.emit(alertIndex);
  }
}
